import { useRef } from 'react'
import { Box, Typography, useMediaQuery, useTheme } from '@mui/material'
import PrimaryButton from '../common/PrimaryButton'
import VideoSection from '../common/VideoSection'
import ScrollBoldText from '../about/ScrollBoldText'
import { aboutMission, aboutVision } from '../../data/aboutContent'
import { useReducedMotion } from '../../hooks/useReducedMotion'
import { useStoryVisionScroll } from '../../hooks/useStoryVisionScroll'
import { BOARD_PIN_TOP_EXTRA_PX } from '../../utils/scrollAnimation'
import {
  HOME_MOBILE_MQ,
  homeBodyTextSx,
  homeMobilePaddingX,
  homeSectionLabelSx,
  homeSectionPy,
} from './homeMobileLayout'

const storyParagraphs = [
  `Since 2011, Disha Foundation has been at the forefront of youth empowerment in Surat and across India. We believe that today's youth are tomorrow's leaders, and our mission is to equip them with the skills, values, and vision needed to build a stronger nation.`,
  `Through our flagship Public Leadership Camp (PLC) and various cultural and social initiatives, we foster leadership qualities, promote good governance, support women empowerment, and inspire young minds to contribute meaningfully to society.`,
]

const PIN_TOP_PADDING = BOARD_PIN_TOP_EXTRA_PX

const storyContentSx = {
  width: '100%',
  maxWidth: { md: 1000, lg: 1000 },
  display: 'flex',
  flexDirection: 'column',
  gap: { xs: '80px', md: '32px' },
  [HOME_MOBILE_MQ]: { gap: '80px' },
}

const panelMotionSx = {
  willChange: 'transform, opacity',
  backfaceVisibility: 'hidden',
}

function StoryBlock({ label, children, showLabelOnMobile = true }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: '24px', md: 0 } }}>
      <Typography
        component="h2"
        sx={{
          ...homeSectionLabelSx,
          display: { xs: showLabelOnMobile ? 'block' : 'none', md: 'none' },
        }}
      >
        {label}
      </Typography>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: { xs: '20px', md: '32px' },
          [HOME_MOBILE_MQ]: { gap: '20px' },
        }}
      >
        {children}
      </Box>
    </Box>
  )
}

function StaticStoryContent() {
  return (
    <>
      {storyParagraphs.map((paragraph) => (
        <Typography key={paragraph.slice(0, 32)} sx={homeBodyTextSx}>
          {paragraph}
        </Typography>
      ))}
      <PrimaryButton
        to="/about-us"
        sx={{
          display: 'inline-flex',
          width: { xs: '100%', md: 151 },
          maxWidth: { xs: '100%', md: 151 },
        }}
      >
        Read More
      </PrimaryButton>
    </>
  )
}

function ScrollStoryVisionLayout({ layerRefs }) {
  return (
    <Box sx={{ position: 'relative', width: '100%' }}>
      <Box aria-hidden sx={{ visibility: 'hidden', pointerEvents: 'none' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
          {storyParagraphs.map((paragraph) => (
            <Typography key={`spacer-story-${paragraph.slice(0, 32)}`} sx={homeBodyTextSx}>
              {paragraph}
            </Typography>
          ))}
        </Box>
      </Box>

      <Box
        ref={(node) => {
          if (layerRefs) layerRefs.current.storyPanel = node
        }}
        sx={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: '32px',
          ...panelMotionSx,
        }}
      >
        {storyParagraphs.map((paragraph, index) => (
          <Box
            key={paragraph.slice(0, 32)}
            ref={(node) => {
              if (layerRefs) layerRefs.current[`storyText${index}`] = node
            }}
          >
            <ScrollBoldText text={paragraph} sx={homeBodyTextSx} />
          </Box>
        ))}
      </Box>

      <Box
        ref={(node) => {
          if (layerRefs) layerRefs.current.visionPanel = node
        }}
        sx={{
          position: 'absolute',
          inset: 0,
          opacity: 0,
          ...panelMotionSx,
        }}
      >
        <Box
          ref={(node) => {
            if (layerRefs) layerRefs.current.visionText = node
          }}
        >
          <ScrollBoldText text={aboutVision} sx={homeBodyTextSx} />
        </Box>
      </Box>
    </Box>
  )
}

export default function OurStorySection() {
  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'))
  const reducedMotion = useReducedMotion()
  const trackRef = useRef(null)
  const pinRef = useRef(null)
  const layerRefs = useRef({
    storyPanel: null,
    visionPanel: null,
    storyText0: null,
    storyText1: null,
    visionText: null,
    storyLabel: null,
    visionLabel: null,
  })

  const scrollActive = isDesktop && !reducedMotion
  useStoryVisionScroll(trackRef, pinRef, layerRefs, scrollActive)

  const sectionSx = {
    width: '100%',
    maxWidth: 1920,
    mx: 'auto',
    px: homeMobilePaddingX,
    py: homeSectionPy,
    boxSizing: 'border-box',
  }

  const rowSx = {
    maxWidth: 1520,
    display: 'flex',
    flexDirection: { xs: 'column', md: 'row' },
    justifyContent: { md: 'space-between' },
    alignItems: 'flex-start',
    gap: { xs: '80px', md: 6, lg: 0 },
    [HOME_MOBILE_MQ]: { gap: '80px' },
  }

  const labelSx = {
    ...homeSectionLabelSx,
    flexShrink: 0,
    display: { xs: 'none', md: 'block' },
  }

  const mobileExtras = (
    <Box sx={{ display: { xs: 'contents', md: 'none' } }}>
      <StoryBlock label="Our Vision">
        <Typography sx={homeBodyTextSx}>{aboutVision}</Typography>
      </StoryBlock>

      <StoryBlock label="Our Mission">
        <Typography sx={homeBodyTextSx}>{aboutMission}</Typography>
      </StoryBlock>

      <Box
        sx={{
          width: '100%',
          borderRadius: '20px',
          overflow: 'hidden',
          '& > div': {
            aspectRatio: { xs: '362 / 200', sm: '16 / 9' },
            borderRadius: '20px',
          },
        }}
      >
        <VideoSection />
      </Box>
    </Box>
  )

  if (!scrollActive) {
    return (
      <Box component="section" sx={sectionSx}>
        <Box sx={rowSx}>
          <Typography component="h2" sx={{ ...labelSx, position: { md: 'sticky' }, top: { md: 100 } }}>
            Our story
          </Typography>

          <Box sx={storyContentSx}>
            <StoryBlock label="Our story">
              <StaticStoryContent />
            </StoryBlock>
            {mobileExtras}
          </Box>
        </Box>
      </Box>
    )
  }

  return (
    <Box component="section" sx={sectionSx}>
      <Box ref={trackRef} sx={{ position: 'relative', width: '100%' }}>
        <Box
          ref={pinRef}
          sx={{
            ...rowSx,
            pt: `${PIN_TOP_PADDING}px`,
            boxSizing: 'border-box',
          }}
        >
          <Box sx={{ position: 'relative', flexShrink: 0 }}>
            <Typography
              component="h2"
              ref={(node) => {
                layerRefs.current.storyLabel = node
              }}
              sx={labelSx}
            >
              Our story
            </Typography>
            <Typography
              component="h2"
              ref={(node) => {
                layerRefs.current.visionLabel = node
              }}
              sx={{
                ...labelSx,
                position: 'absolute',
                top: 0,
                left: 0,
                opacity: 0,
              }}
            >
              Our Vision
            </Typography>
          </Box>

          <Box sx={storyContentSx}>
            <ScrollStoryVisionLayout layerRefs={layerRefs} />
            <StoryBlock label="Our story" />
            <PrimaryButton to="/about-us">
              Read More
            </PrimaryButton>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}
